// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useShellContext } from "@insite/client-framework/Components/IsInShell";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CategoryCard, { CategoryCardStyles } from "@steiner/App/Components/Category/CategoryCard";
import loadFeaturedCategories from "@steiner/App/Handlers/XNG/FeaturedCategories";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    selectedCategories = "selectedCategories",
    linkLabel = "linkLabel",
    link = "link",
    openInNewTab = "openInNewTab",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.selectedCategories]: string[];
        [fields.linkLabel]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, props: OwnProps) => {
    const key = getDataViewKey({
        categories: props.fields.selectedCategories,
    });

    return {
        featuredCategories: state.xFeaturedCategoriesData.data?.[key] || { list: [] },
    };
};

const mapDispatchToProps = {
    loadFeaturedCategories,
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface FeaturedCategoriesStyles {
    container?: InjectableCss;
    title?: TypographyProps;
    categoriesWrapper?: InjectableCss;
    categoryCard?: CategoryCardStyles;
    loadingWrapper?: InjectableCss;
}

export const featuredCategoriesStyles: FeaturedCategoriesStyles = {};

const styles = featuredCategoriesStyles;

const FeaturedCategories: React.FC<Props> = ({
    fields: { title, selectedCategories, linkLabel, link, openInNewTab },
    loadFeaturedCategories,
    featuredCategories,
    history,
}) => {
    const { isInShell } = useShellContext();
    const { url } = useGetLink(link);

    useEffect(() => {
        if (!!selectedCategories.length && !featuredCategories?.isLoading) {
            loadFeaturedCategories({
                categories: selectedCategories,
            });
        }
    }, [selectedCategories]);

    const onHandlerOpenLink = (url: string) => {
        if (url.startsWith("http")) {
            if (isInShell) {
                return;
            }
            openInNewTab ? window.open(url) : (window.location.href = url);
        } else {
            openInNewTab ? window.open(url) : history.push(url);
        }
    };

    if (!featuredCategories.list?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="featuredCategories">
            {title && <Typography {...styles.title}>{title}</Typography>}
            <StyledWrapper {...styles.categoriesWrapper}>
                {featuredCategories.list.map((category: CategoryModel) => (
                    <StyledWrapper key={category.id}>
                        <CategoryCard extendedStyles={styles.categoryCard} category={category} />
                    </StyledWrapper>
                ))}
            </StyledWrapper>
            {url && <Button onClick={() => onHandlerOpenLink(url)}>{linkLabel}</Button>}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(FeaturedCategories)),
    definition: {
        group: XNGAGE_ELEMENTS,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.selectedCategories,
                displayName: "Select Category",
                editorTemplate: "CategoriesField",
                defaultValue: [],
                fieldType: "General",
            },
            {
                name: fields.link,
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
                fieldType: "General",
            },
            {
                name: fields.linkLabel,
                displayName: "Link Label",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isVisible: item => item.fields.link,
            },
            {
                name: fields.openInNewTab,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isVisible: item => item.fields.link,
            },
        ],
    },
};

export default widgetModule;
